import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Icon, Tabs, cn } from '@forecastify/ui';
import {
  CONTROL_PANEL_PATHS,
  operatorAuditExtractResponseSchema,
  operatorFlagsResponseSchema,
  operatorPlanListResponseSchema,
  operatorTenantSchema,
  type FeatureState,
  type OperatorAuditEntry,
  type OperatorPlan,
  type OperatorTenant,
} from '@forecastify/shared';
import { useI18n, useT, type MessageKey } from '../i18n';
import { useOperatorSession } from '../session';
import { TENANTS_PATH } from '../routes';
import { ActionDialog, DialogField } from '../chrome/action-dialog';
import { formatDate, formatMoney, formatNumber } from '../chrome/format';
import { useCrumbs } from '../chrome/page-chrome';
import { useToast } from '../chrome/toasts';
import { BTN, PILL, WARN_BUTTON } from '../chrome/tones';
import { BoundaryPanel } from './boundary-panel';
import { TenantAuditTab } from './tenant-audit-tab';
import { TenantDanger } from './tenant-danger';
import { TenantFeatures } from './tenant-features';
import { TenantOverview } from './tenant-overview';
import { TenantPlan } from './tenant-plan';
import { STATUS_TONE } from './tenants';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ One organisation: a header, five tabs, and the boundary beside them.      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE REDESIGN'S LAYOUT, AND THE FIRST VERSION HAD NONE OF IT.** That one stacked a
 * definition list of slug, counts and dates under a small title, drew the tabs as a pill strip,
 * put a solid red *Suspend* in the corner and the boundary at the bottom of one tab. The mockup
 * draws a 22px title with its status pill, the slug as a chip beside the created date and the plan
 * line, an amber-outlined *Suspend*, underlined tabs on a hairline, and the boundary as a sticky
 * 300px aside beside every tab. Ported here.
 *
 * ⚠️⚠️ **FIVE TABS, NOT THE MOCKUP'S SIX** — `decisions.md` S-16 refuses *Users*: `members` carries
 * a `workspace_id`, and the operator role holds no grant on any such table.
 *
 * ⚠️ **Suspend and Activate both ask first**, in the redesign's one dialog shape. Suspending names
 * its consequence — *Kizárja a tagokat* — and wants a reason of ten characters; activating says the
 * data was never touched. Neither is optimistic: the row on screen is the one the server returned.
 *
 * ⚠️ **The catalogue, flags and trail are fetched HERE rather than passed from the list**, because
 * this address is openable directly — an operator pasting a tenant URL into a ticket is why it is a
 * real URL.
 */

const STATUS_LABEL: Readonly<Record<OperatorTenant['status'], MessageKey>> = {
  trial: 'status.trial',
  active: 'status.active',
  suspended: 'status.suspended',
  awaiting_signup: 'status.awaitingSignup',
};

type Load =
  | { readonly state: 'loading' }
  | { readonly state: 'failed'; readonly code: string }
  | { readonly state: 'ready'; readonly tenant: OperatorTenant };

export type TabId = 'overview' | 'plan' | 'features' | 'audit' | 'danger';

export function TenantDetailScreen(): ReactNode {
  const t = useT();
  const { locale } = useI18n();
  const session = useOperatorSession();
  const toast = useToast();
  /* ⚠️ From `useParams`, which needs this element rendered THROUGH a `<Route>`. `app.tsx` does. */
  const { id } = useParams<{ id: string }>();

  /* ⚠️ Not in the address: a tab is where you are LOOKING, not where you are. */
  const [tab, setTab] = useState<TabId>('overview');
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [plans, setPlans] = useState<readonly OperatorPlan[] | null>(null);
  const [flags, setFlags] = useState<readonly FeatureState[] | null>(null);
  /* ⚠️ `null` while loading and `[]` for "nothing happened", which are different states: on a
     log, empty is a claim. */
  const [trail, setTrail] = useState<readonly OperatorAuditEntry[] | 'failed' | null>(null);
  const [dialog, setDialog] = useState<'suspend' | 'activate' | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const read = useCallback(async (): Promise<void> => {
    if (id === undefined) return;
    const result = await session.request<unknown>(CONTROL_PANEL_PATHS.tenant(id));
    if (!result.ok) {
      if (result.status === 401) return;
      setLoad({ state: 'failed', code: result.code });
      return;
    }
    const parsed = operatorTenantSchema.safeParse(result.value);
    setLoad(
      parsed.success
        ? { state: 'ready', tenant: parsed.data }
        : { state: 'failed', code: 'unknown' },
    );
  }, [id, session]);

  const readFlags = useCallback(async (): Promise<void> => {
    if (id === undefined) return;
    const result = await session.request<unknown>(CONTROL_PANEL_PATHS.flags(id));
    if (!result.ok) return;
    const parsed = operatorFlagsResponseSchema.safeParse(result.value);
    if (parsed.success) setFlags(parsed.data.data);
  }, [id, session]);

  const readTrail = useCallback(async (): Promise<void> => {
    if (id === undefined) return;
    const result = await session.request<unknown>(CONTROL_PANEL_PATHS.tenantAudit(id));
    if (!result.ok) {
      setTrail('failed');
      return;
    }
    const parsed = operatorAuditExtractResponseSchema.safeParse(result.value);
    setTrail(parsed.success ? parsed.data.data : 'failed');
  }, [id, session]);

  const readPlans = useCallback(async (): Promise<void> => {
    const result = await session.request<unknown>(CONTROL_PANEL_PATHS.plans);
    if (!result.ok) return;
    const parsed = operatorPlanListResponseSchema.safeParse(result.value);
    if (parsed.success) setPlans(parsed.data.data);
  }, [session]);

  useEffect(() => {
    setLoad({ state: 'loading' });
    void read();
    void readFlags();
    void readPlans();
    void readTrail();
  }, [read, readFlags, readPlans, readTrail]);

  /** The trail: *Szervezetek › Hegyvidék Studio Kft.* — the second half only this screen knows. */
  useCrumbs(
    load.state === 'ready'
      ? [{ label: t('nav.tenants'), href: TENANTS_PATH }, { label: load.tenant.name }]
      : [{ label: t('nav.tenants'), href: TENANTS_PATH }],
  );

  /** One message per code the API can answer with. Rule 5: branch on `code`, never `message`. */
  const messageFor = (code: string): string => {
    const known: Readonly<Record<string, MessageKey>> = {
      not_found: 'error.not_found',
      already_suspended: 'error.already_suspended',
      already_active: 'error.already_active',
      unknown_plan: 'error.unknown_plan',
      unknown_feature: 'error.unknown_feature',
      already_default: 'error.already_default',
      conflict: 'error.conflict',
      validation_failed: 'error.validation_failed',
      unauthenticated: 'error.unauthenticated',
      unreachable: 'error.unreachable',
      no_contact: 'tenant.contact.none',
    };
    const key = known[code];
    return key === undefined ? t('error.unknown') : t(key);
  };

  /**
   * One shape for every write on this screen: send, replace what is on screen from the response,
   * report a refusal by code.
   *
   * ⚠️ **No optimistic update on any of them**: these change what a customer can do, and showing the
   * new state before the server agreed is the case where being wrong for 200ms matters.
   */
  const write = async (
    path: string,
    method: 'POST' | 'PUT' | 'DELETE',
    body?: unknown,
  ): Promise<boolean> => {
    setBusy(true);
    setFailure(null);
    const result = await session.request<unknown>(path, {
      method,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    setBusy(false);
    if (!result.ok) {
      if (result.status !== 401) setFailure(messageFor(result.code));
      return false;
    }
    const asTenant = operatorTenantSchema.safeParse(result.value);
    if (asTenant.success) {
      setLoad({ state: 'ready', tenant: asTenant.data });
      /* A plan change changes which features the tenant gets. */
      void readFlags();
    }
    const asFlags = operatorFlagsResponseSchema.safeParse(result.value);
    if (asFlags.success) setFlags(asFlags.data.data);
    void readTrail();
    return true;
  };

  if (load.state !== 'ready') {
    return (
      <div data-screen="tenant" className="flex flex-col items-start gap-2 p-6">
        {load.state === 'loading' ? (
          <p role="status" className="text-muted-foreground text-[13px]">
            {t('tenants.loading')}
          </p>
        ) : (
          <div role="alert" className="flex flex-col items-start gap-2">
            <p className="text-[13px]">
              {load.code === 'not_found' ? t('tenant.notFound') : t('tenants.failed')}
            </p>
            {load.code === 'not_found' ? null : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  void read();
                }}
              >
                {t('tenants.retry')}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  const tenant = load.tenant;
  const plan = plans?.find((candidate) => candidate.id === tenant.planId);
  const suspended = tenant.status === 'suspended';
  const reasonShort = 10 - reason.trim().length;

  return (
    <div data-screen="tenant" className="flex flex-col">
      <div className="flex flex-wrap items-start gap-4 px-6 pt-6 pb-4">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-[22px] leading-[28px] font-semibold tracking-[-.015em]">
              {tenant.name}
            </h1>
            <span className={cn(PILL, STATUS_TONE[tenant.status])}>
              {t(STATUS_LABEL[tenant.status])}
            </span>
          </div>
          <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px]">
            <span className="bg-muted text-foreground rounded px-1.5 py-px font-mono text-[11.5px]">
              {tenant.slug}
            </span>
            <span>
              {t('tenant.createdOn', { date: formatDate(tenant.createdAt, locale, true) })}
            </span>
            {plan === undefined ? null : (
              <span>
                {t('tenant.planLine', {
                  plan: plan.name,
                  price: formatMoney(plan.seatPrice, plan.currency, locale),
                })}
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {suspended ? (
            <Button
              variant="outline"
              className={BTN}
              onClick={() => {
                setDialog('activate');
              }}
            >
              {t('tenant.activate')}
            </Button>
          ) : (
            <Button
              variant="outline"
              className={cn(BTN, WARN_BUTTON)}
              onClick={() => {
                setReason('');
                setDialog('suspend');
              }}
            >
              <Icon name="triangle-alert" size={14} />
              {t('tenant.suspend')}
            </Button>
          )}
        </div>
      </div>

      <div className="border-b px-4">
        <Tabs
          variant="underline"
          label={t('tenant.tabs')}
          items={[
            { value: 'overview', label: t('tenant.tab.overview') },
            { value: 'plan', label: t('tenant.tab.plan') },
            { value: 'features', label: t('tenant.tab.features') },
            { value: 'audit', label: t('tenant.tab.audit') },
            { value: 'danger', label: t('tenant.tab.danger') },
          ]}
          value={tab}
          onValueChange={(value) => {
            setTab(value as TabId);
          }}
          className="border-0"
        />
      </div>

      <div className="grid gap-6 p-6 xl:grid-cols-[minmax(0,1fr)_300px]">
        <div className="flex min-w-0 flex-col gap-5">
          {failure === null ? null : (
            <p role="alert" className="text-destructive text-[12.5px]">
              {failure}
            </p>
          )}

          {suspended ? (
            <div className="flex items-start gap-3 rounded-lg border border-[color:var(--status-warn-line)] bg-[var(--status-warn-soft)] p-3.5">
              <Icon
                name="triangle-alert"
                size={16}
                className="mt-px shrink-0 text-[var(--status-warn-text)]"
              />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-[13px] font-medium text-[var(--status-warn-text)]">
                  {t('tenant.suspendedSince', { date: formatDate(tenant.suspendedAt, locale) })}
                </span>
                {tenant.suspensionReason === null ? null : (
                  <span className="text-[12.5px] text-pretty">{tenant.suspensionReason}</span>
                )}
              </div>
            </div>
          ) : null}

          {tab === 'overview' ? (
            <TenantOverview
              tenant={tenant}
              plan={plan}
              flags={flags}
              trail={trail}
              onGoToTab={setTab}
              onChanged={() => {
                void read();
              }}
              onContactReset={() => {
                void session
                  .request<unknown>(CONTROL_PANEL_PATHS.contactPasswordReset(tenant.id), {
                    method: 'POST',
                  })
                  .then((result) => {
                    if (!result.ok) {
                      if (result.status !== 401) setFailure(messageFor(result.code));
                      return;
                    }
                    toast({
                      title: t('tenant.contact.resetSent'),
                      description: tenant.contactEmail ?? '',
                    });
                    void readTrail();
                  });
              }}
            />
          ) : null}

          {tab === 'plan' ? (
            <TenantPlan tenant={tenant} plans={plans} busy={busy} write={write} />
          ) : null}

          {tab === 'features' ? (
            <TenantFeatures tenant={tenant} flags={flags} busy={busy} write={write} />
          ) : null}

          {tab === 'audit' ? <TenantAuditTab tenant={tenant} trail={trail} /> : null}

          {tab === 'danger' ? <TenantDanger tenant={tenant} plan={plan} /> : null}
        </div>

        <BoundaryPanel />
      </div>

      <ActionDialog
        open={dialog === 'suspend'}
        onClose={() => {
          setDialog(null);
        }}
        title={t('tenant.suspend.title', { name: tenant.name })}
        description={t('tenant.suspend.lede', {
          people: formatNumber(tenant.memberCount, locale),
          workspaces: formatNumber(tenant.workspaceCount, locale),
        })}
        tier={{ label: t('tenant.suspend.tier'), tone: 'warn' }}
        cancelLabel={t('tenant.suspend.cancel')}
        blockedText={reasonShort > 0 ? t('tenant.suspend.short', { count: reasonShort }) : ''}
        confirm={{
          label: busy ? t('tenant.suspend.working') : t('tenant.suspend.confirm'),
          tone: 'warn',
          disabled: busy || reasonShort > 0,
          onClick: () => {
            void write(CONTROL_PANEL_PATHS.suspend(tenant.id), 'POST', {
              reason: reason.trim(),
            }).then((ok) => {
              if (!ok) return;
              setDialog(null);
              toast({ title: t('tenant.suspend.done'), description: tenant.name });
            });
          },
        }}
      >
        <DialogField label={t('tenant.suspend.reason')} htmlFor="suspend-reason">
          <textarea
            id="suspend-reason"
            className="bg-muted/50 hover:border-input focus:border-ring focus:ring-ring/30 min-h-[80px] rounded-md border border-transparent px-2.5 py-2 text-[13px] outline-none focus:ring-[3px]"
            value={reason}
            placeholder={t('tenant.suspend.reasonHint')}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
        </DialogField>
      </ActionDialog>

      <ActionDialog
        open={dialog === 'activate'}
        onClose={() => {
          setDialog(null);
        }}
        title={t('tenant.activate.title', { name: tenant.name })}
        description={t('tenant.activate.lede')}
        cancelLabel={t('tenant.suspend.cancel')}
        confirm={{
          label: busy ? t('tenant.activate.working') : t('tenant.activate'),
          disabled: busy,
          onClick: () => {
            void write(CONTROL_PANEL_PATHS.activate(tenant.id), 'POST').then((ok) => {
              if (!ok) return;
              setDialog(null);
              toast({ title: t('tenant.activate.done'), description: tenant.name });
            });
          },
        }}
      />
    </div>
  );
}
